import { makeAutoObservable, runInAction } from "mobx";
import {
  RateEntity,
  FeeEntity,
  WalletEntity,
  DocumentDto,
  CompanySettingsEntity,
  RequestFeeSettingsDto,
  COMPANY_ROLE_ENUM,
} from "interfaces";
import { NormalizedError } from "services/api/errorHandler";
import { statusFetching } from "services/authClient/constants/types";
import { getCompanyFees, getProviderWallets, getRates, getRequestFeeSettings } from "services/settingsService";
import { api, ENDPOINTS, getCompanySettings, getRuleFiles } from "services";
import { handleFetchError } from "utils/errors";

export class SettingsStore {
  public requestFeeSettings: RequestFeeSettingsDto[] = [
    {
      role: COMPANY_ROLE_ENUM.CUSTOMER,
      direction: "IMP",
      amountDiscount: 0,
      discountThreshold: 0,
      acquiringFee: 0,
      agentFee: 0,
      additionalRequestsFee: 0,
    },
    {
      role: COMPANY_ROLE_ENUM.CUSTOMER,
      direction: "EXP",
      amountDiscount: 0,
      discountThreshold: 0,
      acquiringFee: 0,
      agentFee: 0,
      additionalRequestsFee: 0,
    },
    {
      role: COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER,
      direction: "IMP",
      amountDiscount: 0,
      discountThreshold: 0,
      acquiringFee: 0,
      agentFee: 0,
      additionalRequestsFee: 0,
    },
    {
      role: COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER,
      direction: "EXP",
      amountDiscount: 0,
      discountThreshold: 0,
      acquiringFee: 0,
      agentFee: 0,
      additionalRequestsFee: 0,
    },
  ];
  public companyFees: FeeEntity = {
    customerBaseFeeImp: 0,
    customerBaseFeeExp: 0,
    lpBaseFeeImp: 0,
    lpBaseFeeExp: 0,
  };

  public companySettings: CompanySettingsEntity = {
    doubleActionsValidation: false,
    destinationWalletVerification: false,
    doubleActionsValidationThreshold: 0,
    whitelistEnabled: false,
  };

  public rates: RateEntity = {
    rates: {},
  };

  public ruleFiles: DocumentDto[] = [];

  public ruleFilesLoading: boolean = false;

  public providerWallets: WalletEntity[] = [];

  public infoText: string = "";

  public fetchStatus: statusFetching = "init";
  public fetchError?: NormalizedError;

  constructor() {
    makeAutoObservable(this);
  }

  get isPending() {
    return this.fetchStatus === "fetching";
  }

  fetchCompanySettings = async (id: string) => {
    return getCompanySettings(id).then((res) => {
      runInAction(() => {
        this.companySettings = res;
      });
    });
  };

  fetchCompanyFees = async () => {
    runInAction(() => {
      this.fetchStatus = "fetching";
      this.fetchError = undefined;
    });

    return getCompanyFees()
      .then((res) => {
        runInAction(() => {
          this.companyFees = res;
          this.fetchStatus = "success";
        });
      })
      .catch((error: NormalizedError) => {
        runInAction(() => {
          this.fetchStatus = "error";
          this.fetchError = error;
        });
      });
  };

  fetchRates = async () => {
    runInAction(() => {
      this.fetchStatus = "fetching";
      this.fetchError = undefined;
    });

    return getRates()
      .then((res) => {
        runInAction(() => {
          this.rates = res;
          this.fetchStatus = "success";
        });
      })
      .catch((error: NormalizedError) => {
        runInAction(() => {
          this.fetchStatus = "error";
          this.fetchError = error;
        });
      });
  };

  fetchRuleFiles = async () => {
    if (this.ruleFilesLoading) {
      return Promise.resolve(this.ruleFiles);
    }

    runInAction(() => {
      this.ruleFilesLoading = true;
      this.fetchStatus = "fetching";
      this.fetchError = undefined;
    });

    return getRuleFiles()
      .then((res) => {
        runInAction(() => {
          this.ruleFiles = res.sort((a, b) => a.docName.localeCompare(b.docName));
          this.ruleFilesLoading = false;
          this.fetchStatus = "success";
        });
        return this.ruleFiles;
      })
      .catch((error: NormalizedError) => {
        runInAction(() => {
          this.ruleFilesLoading = false;
          this.fetchStatus = "error";
          this.fetchError = error;
        });
        throw error;
      });
  };

  // fetchWallets = async (id: string) => {
  //   runInAction(() => {
  //     this.fetchStatus = "fetching";
  //     this.fetchError = undefined;
  //   });

  //   return getProviderWallets({ id })
  //     .then((res) => {
  //       runInAction(() => {
  //         this.providerWallets = res;
  //         this.fetchStatus = "success";
  //       });
  //     })
  //     .catch((error: NormalizedError) => {
  //       runInAction(() => {
  //         this.fetchStatus = "error";
  //         this.fetchError = error;
  //       });
  //     });
  // };

  fetchSettingsInfoText = async () => {
    runInAction(() => {
      this.fetchStatus = "fetching";
      this.fetchError = undefined;
    });

    try {
      const response = await fetch(ENDPOINTS.settingsInfoText(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${api.getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const infoText = await response.text();

      runInAction(() => {
        this.infoText = infoText;
        this.fetchStatus = "success";
      });

      return infoText;
    } catch (error: any) {
      const normalizedError = handleFetchError(error);
      runInAction(() => {
        this.fetchStatus = "error";
        this.fetchError = normalizedError;
      });
      throw normalizedError;
    }
  };

  fetchRequestFeeSettings = async () => {
    // TODO: Когда боевой API будет готов, добавить полноценную обработку success/error
    // для fetchRequestFeeSettings и не оставлять общий fetchStatus в состоянии fetching.
    runInAction(() => {
      this.fetchStatus = "fetching";
      this.fetchError = undefined;
    });

    return getRequestFeeSettings().then((res) => {
      runInAction(() => {
        this.requestFeeSettings = res;
      });
    });
  };
}
